import type { ExpandedCareInfo, ExpandedAccordionSection } from '../../types/careExpanded';
import { CARE_SECTION_CONFIG } from '../../types/careExpanded';
import { LightSection } from './LightSection';
import { WateringSection } from './WateringSection';
import { FeedingSection } from './FeedingSection';
import { PottingSection } from './PottingSection';
import { SoilRecipesSection } from './SoilRecipesSection';
import { PruningSection } from './PruningSection';
import { PropagationSection } from './PropagationSection';
import { TroubleshootingSection } from './TroubleshootingSection';
import { SeasonalSection } from './SeasonalSection';
import { NotesSection } from './NotesSection';

interface ExpandedCareAccordionProps {
  careInfo: ExpandedCareInfo;
  expandedSections: Set<ExpandedAccordionSection>;
  onToggle: (section: ExpandedAccordionSection) => void;
  onSectionRef?: (key: string, el: HTMLDivElement | null) => void;
}

/**
 * Accordion component for displaying expanded v2 care info.
 * Each section is collapsible and displays detailed plant care information.
 */
export function ExpandedCareAccordion({
  careInfo,
  expandedSections,
  onToggle,
  onSectionRef,
}: ExpandedCareAccordionProps) {
  // Build accordion items in display order
  const sections: {
    key: ExpandedAccordionSection;
    content: React.ReactNode;
    available: boolean;
  }[] = [
    {
      key: 'light',
      content: <LightSection light={careInfo.light} />,
      available: true,
    },
    {
      key: 'watering',
      content: <WateringSection watering={careInfo.watering} />,
      available: true,
    },
    {
      key: 'feeding',
      content: <FeedingSection feeding={careInfo.feeding} />,
      available: true,
    },
    {
      key: 'potting',
      content: <PottingSection potting={careInfo.potting} />,
      available: true,
    },
    {
      key: 'soilRecipes',
      content: <SoilRecipesSection recipes={careInfo.soilRecipes} />,
      available: careInfo.soilRecipes.length > 0,
    },
    {
      key: 'pruning',
      content: <PruningSection pruning={careInfo.pruning} />,
      available: true,
    },
    {
      key: 'propagation',
      content: <PropagationSection propagation={careInfo.propagation} />,
      available: careInfo.propagation.methods.length > 0,
    },
    {
      key: 'troubleshooting',
      content: <TroubleshootingSection items={careInfo.troubleshooting} />,
      available: careInfo.troubleshooting.length > 0,
    },
    {
      key: 'seasonal',
      content: careInfo.seasonal ? <SeasonalSection seasonal={careInfo.seasonal} /> : null,
      available: !!careInfo.seasonal && Object.values(careInfo.seasonal).some(Boolean),
    },
    {
      key: 'notes',
      content: careInfo.notes ? <NotesSection notes={careInfo.notes} /> : null,
      available: !!careInfo.notes && (!!careInfo.notes.ai || !!careInfo.notes.user),
    },
  ];

  // Filter to available sections and sort by order
  const availableSections = sections
    .filter((s) => s.available)
    .sort((a, b) => CARE_SECTION_CONFIG[a.key].order - CARE_SECTION_CONFIG[b.key].order);

  return (
    <div className="space-y-2">
      {availableSections.map(({ key, content }) => {
        const config = CARE_SECTION_CONFIG[key];
        const isOpen = expandedSections.has(key);
        const panelId = `expanded-accordion-panel-${key}`;
        const headerId = `expanded-accordion-header-${key}`;

        return (
          <div
            key={key}
            ref={(el) => onSectionRef?.(key, el)}
            className="overflow-hidden rounded-lg border border-gray-200 bg-white"
          >
            <button
              id={headerId}
              type="button"
              onClick={() => onToggle(key)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onToggle(key);
                }
              }}
              className="flex w-full min-h-[44px] items-center justify-between px-4 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
              aria-expanded={isOpen}
              aria-controls={panelId}
            >
              <div className="flex items-center gap-3">
                <span className="material-symbols-outlined text-primary" style={{ fontSize: 22 }} aria-hidden="true">
                  {config.icon}
                </span>
                <span className="font-display font-bold text-editorial-black">{config.label}</span>
              </div>
              <span
                className={`material-symbols-outlined text-gray-400 transition-transform ${
                  isOpen ? 'rotate-180' : ''
                }`}
                style={{ fontSize: 20 }}
                aria-hidden="true"
              >
                expand_more
              </span>
            </button>

            <div
              id={panelId}
              role="region"
              aria-labelledby={headerId}
              className={`border-t border-gray-100 px-4 py-3 transition-all ${
                isOpen ? 'block' : 'hidden'
              }`}
            >
              {content}
            </div>
          </div>
        );
      })}
    </div>
  );
}
